import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Languages, Loader2, Mic, MessageSquare, Globe, Zap, Shield, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import appIcon from '@/assets/app-icon.png';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageCode } from '@/i18n/translations';
import { useAuth } from '@/contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'ru', name: 'Русский' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'zh', name: '中文' },
  { code: 'ar', name: 'العربية' },
  { code: 'hi', name: 'हिन्दी' },
];

const Onboarding = () => {
  const { language, setLanguage, t } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(language);
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const hasCheckedRef = useRef(false);

  // Auto-redirect logged-in users to chats with retry for native platforms
  useEffect(() => {
    if (hasCheckedRef.current) return;
    
    const checkAndRedirect = async () => {
      // On native platforms, storage is async - retry a few times
      const isNative = Capacitor.isNativePlatform();
      const maxRetries = isNative ? 5 : 1;
      const retryDelay = 300; // ms
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        // Wait for auth loading to complete
        if (loading) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
        
        // Check session directly from Supabase (never hang on a stalled read)
        const { data: { session } } = await Promise.race([
          supabase.auth.getSession(),
          new Promise<any>((resolve) =>
            setTimeout(() => resolve({ data: { session: null } }), 6000)
          ),
        ]);
        
        if (session?.user) {
          console.log('[Onboarding] Session found, redirecting to chats');
          hasCheckedRef.current = true;
          navigate('/chats', { replace: true });
          return;
        }
        
        // If no session and we're on native, wait and retry
        if (isNative && attempt < maxRetries - 1) {
          console.log(`[Onboarding] No session yet, retry ${attempt + 1}/${maxRetries}`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
      
      // No session found after retries
      console.log('[Onboarding] No session found, showing welcome screen');
      hasCheckedRef.current = true;
      setCheckingAuth(false);
    };
    
    checkAndRedirect();
  }, [loading, navigate]);

  // Also respond to user changes from AuthContext
  useEffect(() => {
    if (!loading && user && !checkingAuth) {
      navigate('/chats', { replace: true });
    }
  }, [user, loading, checkingAuth, navigate]);

  const handleLanguageChange = (value: string) => {
    const lang = value as LanguageCode;
    setSelectedLanguage(lang);
    setLanguage(lang);
  };

  const handleContinue = () => {
    localStorage.setItem('preferredLanguage', selectedLanguage);
    navigate('/auth');
  };

  // Show loading while checking auth status
  if (checkingAuth) {
    return (
      <div className="safe-area-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const features = [
    { icon: Mic, title: 'AI Voice Translation', desc: 'Send voice messages in your language — recipients hear them translated instantly.' },
    { icon: MessageSquare, title: 'Real-Time Text Translation', desc: 'Every text message is automatically translated to each participant\'s language.' },
    { icon: Globe, title: '12+ Languages', desc: 'Supports English, Spanish, French, German, Japanese, Chinese, Arabic, and more.' },
    { icon: Zap, title: 'Instant & Seamless', desc: 'Powered by AI for lightning-fast translations that feel natural.' },
    { icon: Shield, title: 'Private & Secure', desc: 'End-to-end encrypted chats. Your conversations stay yours.' },
    { icon: Users, title: 'Group Chats', desc: 'Create multilingual group chats where everyone reads in their own language.' },
  ];

  return (
    <div className="safe-area-screen flex flex-col bg-gradient-to-br from-primary/5 via-background to-accent/5 overflow-y-auto pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 px-[max(env(safe-area-inset-left),1rem)] pr-[max(env(safe-area-inset-right),1rem)] relative">
        {/* Floating decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-[10%] left-[8%] w-20 h-20 rounded-2xl bg-primary/10 blur-sm"
          />
          <motion.div
            animate={{ y: [0, 15, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute top-[15%] right-[10%] w-16 h-16 rounded-full bg-accent/10 blur-sm"
          />
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
            className="absolute bottom-[20%] left-[12%] w-12 h-12 rounded-lg bg-accent/15 blur-sm"
          />
          <motion.div
            animate={{ y: [0, 12, 0], x: [0, -5, 0] }}
            transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            className="absolute bottom-[25%] right-[8%] w-24 h-14 rounded-2xl bg-primary/8 blur-sm"
          />
        </div>

        {/* Tagline above the card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6 max-w-2xl"
        >
          <h2 className="text-lg font-medium text-muted-foreground tracking-wide uppercase">
            AI-Powered Multilingual Chat
          </h2>
          <p className="text-4xl md:text-5xl font-bold text-foreground mt-2 leading-tight">
            Speak any language.<br />
            <span className="text-primary">Connect with everyone.</span>
          </p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="w-full max-w-md p-8 space-y-6 shadow-xl border-border/50 backdrop-blur-sm">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full overflow-hidden mb-4">
                <img src={appIcon} alt="Global Chat Connect" className="w-16 h-16 object-cover" />
              </div>
              <h1 className="text-3xl font-bold">{t('welcome')}</h1>
              <p className="text-muted-foreground">
                {t('welcomeSubtitle')}
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="language" className="flex items-center gap-2">
                  <Languages className="w-4 h-4" />
                  {t('selectLanguage')}
                </Label>
                <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                  <SelectTrigger id="language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {t('languageHint')}
                </p>
              </div>

              <Button onClick={handleContinue} className="w-full" size="lg">
                {t('continue')}
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* App Store Badges */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="https://apps.apple.com/us/app/global-chat-connect/id6755732689"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Global Chat Connect on the App Store"
          >
            <img
              src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
              alt="Download on the App Store"
              className="h-14 w-auto"
              loading="lazy"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=app.lovable.c16458328a2848af9115a825783212c2&pcampaignid=web_share"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get Global Chat Connect on Google Play"
          >
            <img
              src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
              alt="Get it on Google Play"
              className="h-[68px] w-auto -my-1.5"
              loading="lazy"
            />
          </a>
        </motion.div>

        {/* SEO copy */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-10 max-w-2xl text-center px-4"
        >
          <h2 className="text-xl font-semibold text-foreground mb-3">
            Real-time message translation in 12+ languages
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Global Chat Connect is an AI-powered messaging app that automatically translates
            text and voice messages between English, Spanish, French, German, Italian,
            Portuguese, Russian, Japanese, Korean, Chinese, Arabic, and Hindi. Chat with
            anyone, anywhere — each person reads and hears messages in their own language.
            Free to try with a 3-day trial.
          </p>
        </motion.section>
      </div>

      {/* Features Section */}
      <div className="w-full max-w-5xl mx-auto px-6 pb-12 pt-8">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-muted-foreground text-sm mb-8 uppercase tracking-widest font-medium"
        >
          Why Global Chat Connect?
        </motion.p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
            >
              <Card className="p-5 h-full border-border/40 hover:border-primary/30 transition-colors group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">{feature.title}</h3>
                    <p className="text-muted-foreground text-xs mt-1 leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
